import React, { useState, useEffect, useRef, useCallback } from 'react';
import './GestureInterface.css';
import mockData from './mockData.json';
import GestureDetector from './GestureDetector';
import SimpleCameraGestures from './SimpleCameraGestures';
import SimpleMediaPipe from './SimpleMediaPipe';

const GestureInterface = () => {
  const canvasRef = useRef(null);
  const [currentContact, setCurrentContact] = useState(0);
  const [currentCase, setCurrentCase] = useState(0);
  const [currentEntity, setCurrentEntity] = useState(0);
  const [gestureDetectorType, setGestureDetectorType] = useState('simple_mediapipe'); // 'mediapipe', 'simple', or 'simple_mediapipe'
  
  // Original state variables
  const [selectedContact, setSelectedContact] = useState(null);
  const [selectedCase, setSelectedCase] = useState(null);
  const [currentView, setCurrentView] = useState('contact');
  const [navigableItems, setNavigableItems] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [instructionsMinimized, setInstructionsMinimized] = useState(false);
  const [gesturePosition, setGesturePosition] = useState({ x: window.innerWidth - 220, y: 20 });
  const [isDragging, setIsDragging] = useState(false);
  const [dragOffset, setDragOffset] = useState({ x: 0, y: 0 });

  // Initialize data
  useEffect(() => {
    // Start with contact view - James Miller is the main contact
    const contact = mockData.contact;
    setSelectedContact(contact);
    setNavigableItems(contact.cases);
    setCurrentIndex(0);
  }, []);

  // Navigate left/right
  const navigateLeft = useCallback(() => {
    if (navigableItems.length > 0) {
      setCurrentIndex(prev => prev > 0 ? prev - 1 : navigableItems.length - 1);
    }
  }, [navigableItems.length]);

  const navigateRight = useCallback(() => {
    if (navigableItems.length > 0) {
      setCurrentIndex(prev => prev < navigableItems.length - 1 ? prev + 1 : 0);
    }
  }, [navigableItems.length]);

  // Select current item (finger point)
  const selectCurrentItem = useCallback(() => {
    if (currentView === 'contact') {
      // Select case from contact view
      const selectedCaseItem = navigableItems[currentIndex];
      setSelectedCase(selectedCaseItem);
      setCurrentView('case');
      // Set navigable items to case-related entities
      const caseEntities = [
        ...selectedCaseItem.persons.map(p => ({ ...p, type: 'person' })),
        ...selectedCaseItem.assessments.map(a => ({ ...a, type: 'assessment' })),
        ...selectedCaseItem.courtHearings.map(h => ({ ...h, type: 'hearing' })),
        ...selectedCaseItem.clientContacts.map(c => ({ ...c, type: 'contact' }))
      ];
      setNavigableItems(caseEntities);
      setCurrentIndex(0);
    }
  }, [currentView, navigableItems, currentIndex]);

  // Go back (closed fist)
  const goBack = useCallback(() => {
    if (currentView === 'case') {
      setCurrentView('contact');
      setSelectedCase(null);
      setNavigableItems(selectedContact.cases);
      setCurrentIndex(0);
    }
  }, [currentView, selectedContact]);

    // Handle gesture detection errors and fallback
  const handleGestureDetectorError = useCallback(() => {
    console.log('Switching gesture detector due to error');
    if (gestureDetectorType === 'mediapipe') {
      setGestureDetectorType('simple_mediapipe');
    } else if (gestureDetectorType === 'simple_mediapipe') {
      setGestureDetectorType('simple');
    }
  }, [gestureDetectorType]);

  // Handle gestures from any detector
  const handleGesture = useCallback((gesture) => {
    console.log('Detected gesture:', gesture);
    
    switch (gesture) {
      case 'swipe_left':
        navigateLeft();
        break;
      case 'swipe_right':
        navigateRight();
        break;
      case 'point':
        selectCurrentItem();
        break;
      case 'peace':
        goBack();
        break;
      default:
        break;
    }
  }, [navigateLeft, navigateRight, selectCurrentItem, goBack]);

  // Get current item details for display
  const getCurrentItemDetails = () => {
    if (currentView === 'contact') {
      return {
        title: `${selectedContact?.firstName} ${selectedContact?.lastName}`,
        subtitle: selectedContact?.role || 'Contact',
        details: {
          'Contact ID': selectedContact?.contactId,
          'Email': selectedContact?.contactInfo?.email,
          'Phone': selectedContact?.contactInfo?.phone,
          'Address': `${selectedContact?.contactInfo?.address?.street1}, ${selectedContact?.contactInfo?.address?.city}`,
          'Cases': selectedContact?.cases?.length || 0
        }
      };
    } else {
      // In case view, show details of the currently selected entity
      const currentItem = navigableItems[currentIndex];
      
      if (currentItem?.type === 'person') {
        return {
          title: `${currentItem.firstName} ${currentItem.lastName}`,
          subtitle: `Role: ${currentItem.relationshipToContact}`,
          details: {
            'Person ID': currentItem.personId,
            'Date of Birth': currentItem.dob,
            'Gender': currentItem.gender,
            'Race/Ethnicity': currentItem.raceEthnicity,
            'Email': currentItem.contactInfo?.email || 'N/A',
            'Phone': currentItem.contactInfo?.phone || 'N/A',
            'Address': currentItem.contactInfo?.address ? 
              `${currentItem.contactInfo.address.street1}, ${currentItem.contactInfo.address.city}` : 'N/A'
          }
        };
      } else if (currentItem?.type === 'assessment') {
        return {
          title: `${currentItem.assessmentType} Assessment`,
          subtitle: `Tool: ${currentItem.toolUsed}`,
          details: {
            'Assessment ID': currentItem.assessmentId,
            'Date': currentItem.assessmentDate,
            'Tool Used': currentItem.toolUsed,
            'Completed By': currentItem.completedBy,
            'Findings': currentItem.findings?.length || 0,
            'Status': 'Completed'
          }
        };
      } else if (currentItem?.type === 'hearing') {
        return {
          title: 'Court Hearing',
          subtitle: `Type: ${currentItem.type}`,
          details: {
            'Hearing ID': currentItem.hearingId,
            'Date': currentItem.date,
            'Type': currentItem.type,
            'Judge': currentItem.judge,
            'Outcome': currentItem.outcome,
            'Attendees': currentItem.attendees?.length || 0
          }
        };
      } else if (currentItem?.type === 'contact') {
        return {
          title: 'Client Contact',
          subtitle: `Method: ${currentItem.method}`,
          details: {
            'Contact ID': currentItem.contactInterviewId,
            'Date': currentItem.dateTime?.split('T')[0],
            'Method': currentItem.method,
            'Location': currentItem.location?.city || 'N/A',
            'Participants': currentItem.participants?.length || 0,
            'Summary': currentItem.summary?.substring(0, 50) + '...' || 'N/A'
          }
        };
      } else {
        // Fallback - show the selected case details
        return {
          title: selectedCase?.caseName || 'Case',
          subtitle: `Case Status: ${selectedCase?.caseStatus}`,
          details: {
            'Case ID': selectedCase?.caseId,
            'Program Type': selectedCase?.programType,
            'Risk Level': selectedCase?.riskLevel,
            'Opened': selectedCase?.openedDate,
            'Next Review': selectedCase?.nextReviewDate,
            'Persons': selectedCase?.persons?.length || 0,
            'Assessments': selectedCase?.assessments?.length || 0
          }
        };
      }
    }
  };

  const currentDetails = getCurrentItemDetails();

  // Keyboard navigation
  useEffect(() => {
    const handleKeyPress = (event) => {
      switch (event.key) {
        case 'ArrowLeft':
          event.preventDefault();
          navigateLeft();
          break;
        case 'ArrowRight':
          event.preventDefault();
          navigateRight();
          break;
        case 'Enter':
        case ' ': // Spacebar
          event.preventDefault();
          selectCurrentItem();
          break;
        case 'Escape':
        case 'Backspace':
          event.preventDefault();
          goBack();
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyPress);
    return () => window.removeEventListener('keydown', handleKeyPress);
  }, [navigateLeft, navigateRight, selectCurrentItem, goBack]);

  // Drag handlers for gesture detection component
  const handleMouseDown = useCallback((e) => {
    setIsDragging(true);
    const rect = e.currentTarget.getBoundingClientRect();
    setDragOffset({
      x: e.clientX - rect.left,
      y: e.clientY - rect.top
    });
  }, []);

  const handleMouseMove = useCallback((e) => {
    if (isDragging) {
      const newX = Math.max(0, Math.min(window.innerWidth - 200, e.clientX - dragOffset.x));
      const newY = Math.max(0, Math.min(window.innerHeight - 150, e.clientY - dragOffset.y));
      setGesturePosition({ x: newX, y: newY });
    }
  }, [isDragging, dragOffset]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  // Add global mouse event listeners for dragging
  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      return () => {
        document.removeEventListener('mousemove', handleMouseMove);
        document.removeEventListener('mouseup', handleMouseUp);
      };
    }
  }, [isDragging, handleMouseMove, handleMouseUp]);

  return (
    <div className="gesture-interface" ref={canvasRef}>
      {/* App Logo */}
      <div className="app-logo">
        <div className="logo-icon">AI</div>
        <div className="logo-text">
          <div className="logo-title">Augmented Interfaces</div>
          <div className="logo-subtitle">Case Management System</div>
        </div>
      </div>

      {/* Gesture Detection */}
      <div 
        className={`gesture-detector-container ${isDragging ? 'dragging' : ''}`}
        style={{
          position: 'fixed',
          left: gesturePosition.x,
          top: gesturePosition.y,
          zIndex: 1001,
          cursor: isDragging ? 'grabbing' : 'grab'
        }}
        onMouseDown={handleMouseDown}
      >
        <div className="drag-handle">
          <span className="drag-dots">⋮⋮</span>
        </div>
        {gestureDetectorType === 'mediapipe' ? (
          <GestureDetector 
            onGesture={handleGesture} 
            onError={handleGestureDetectorError}
          />
        ) : gestureDetectorType === 'simple_mediapipe' ? (
          <SimpleMediaPipe
            onGesture={handleGesture} 
            onError={handleGestureDetectorError}
          />
        ) : (
          <SimpleCameraGestures onGesture={handleGesture} />
        )}
      </div>

      {/* Layer 1: Details Display (Top) */}
      <div className="layer layer-details">
        <div className="details-panel">
          <div className="details-header">
            <h2 className="details-title">{currentDetails.title}</h2>
            <p className="details-subtitle">{currentDetails.subtitle}</p>
          </div>
          <div className="details-grid">
            {Object.entries(currentDetails.details).map(([key, value]) => (
              <div key={key} className="detail-item">
                <span className="detail-label">{key}</span>
                <span className="detail-value">{value}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Layer 2: File Stack (Middle) */}
      <div className="layer layer-stack">
        <div className="file-stack">
          {/* Stack indicator */}
          <div className="stack-indicator">
            <div className="stack-count">
              {navigableItems.length} files
            </div>
            <div className="stack-position">
              {currentIndex + 1} of {navigableItems.length}
            </div>
          </div>

          {navigableItems.map((item, index) => {
            const isActive = index === currentIndex;
            const offset = index - currentIndex;
            const absOffset = Math.abs(offset);
            
            // Show up to 3 cards on each side of the active card
            const isVisible = absOffset <= 3;
            
            return (
              <div
                key={item.caseId || item.personId || item.assessmentId || index}
                className={`file-card ${isActive ? 'active' : ''}`}
                style={{
                  transform: `
                    translateZ(${isActive ? 80 : 40 - (absOffset * 15)}px)
                    translateX(${offset * 80}px)
                    rotateY(${offset * 8}deg)
                    scale(${isActive ? 1 : Math.max(0.7, 1 - (absOffset * 0.1))})
                  `,
                  opacity: isVisible ? (isActive ? 1 : Math.max(0.4, 1 - (absOffset * 0.2))) : 0,
                  zIndex: 100 - absOffset,
                  cursor: 'pointer',
                  visibility: isVisible ? 'visible' : 'hidden'
                }}
                onClick={() => {
                  // Navigate to clicked card
                  setCurrentIndex(index);
                }}
                onDoubleClick={() => {
                  // Double-click to select (same as pointing gesture)
                  setCurrentIndex(index);
                  setTimeout(() => selectCurrentItem(), 100);
                }}
              >
                <div className="file-card-content">
                  <h4>
                    {item.caseName || 
                     (item.firstName && item.lastName ? `${item.firstName} ${item.lastName}` : 
                     (item.type === 'assessment' ? `${item.assessmentType} Assessment` :
                     (item.type === 'hearing' ? 'Court Hearing' :
                     (item.type === 'contact' ? 'Client Contact' : item.type))))}
                  </h4>
                  <p>
                    {item.caseStatus || 
                     item.relationshipToContact || 
                     (item.type === 'assessment' ? `Tool: ${item.toolUsed}` :
                     (item.type === 'hearing' ? `${item.type} - ${item.date?.split('T')[0]}` :
                     (item.type === 'contact' ? `${item.method} - ${item.dateTime?.split('T')[0]}` : 'Entity')))}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Layer 3: Compass/Pivot (Bottom) */}
      <div className="layer layer-compass">
        <div className="compass-container">
          <div className="compass-background">
            <div 
              className="compass-needle"
              style={{
                transform: `translateX(-50%) translateY(-100%) rotate(${(currentIndex / Math.max(navigableItems.length - 1, 1)) * 180}deg)`
              }}
            />
            <div className="compass-center">
              <div className="compass-icon">
                {currentView === 'contact' ? 
                  `${selectedContact?.firstName?.[0]}${selectedContact?.lastName?.[0]}` :
                  selectedCase?.caseName?.split(' ')[0]?.[0] + selectedCase?.caseName?.split(' ')[1]?.[0]
                }
              </div>
            </div>
          </div>
          <div className="position-indicator">
            {navigableItems.length > 0 && `${currentIndex + 1}/${navigableItems.length}`}
          </div>
        </div>
      </div>

      {/* Navigation Controls */}
      <div className="gesture-controls">
        <button onClick={navigateLeft} className="gesture-btn" title="Navigate Left (Arrow Left)">
          ← Previous
        </button>
        <button onClick={selectCurrentItem} className="gesture-btn select-btn" title="Select Current Item (Enter/Space)">
          ✨ Select
        </button>
        <button onClick={navigateRight} className="gesture-btn" title="Navigate Right (Arrow Right)">
          Next →
        </button>
        <button onClick={goBack} className="gesture-btn back-btn" title="Go Back (Escape/Backspace)">
          ↩️ Back
        </button>
        <button 
          onClick={() => {
            const nextType = gestureDetectorType === 'mediapipe' ? 'simple_mediapipe' : 
                            gestureDetectorType === 'simple_mediapipe' ? 'simple' : 'mediapipe';
            setGestureDetectorType(nextType);
          }}
          className="gesture-btn toggle-btn" 
          title="Cycle through gesture detection methods"
        >
          🔄 {gestureDetectorType === 'mediapipe' ? 'MediaPipe' : 
               gestureDetectorType === 'simple_mediapipe' ? 'Simple MP' : 'Simple'}
        </button>
      </div>

      {/* Gesture Instructions */}
      <div className={`gesture-instructions ${instructionsMinimized ? 'minimized' : ''}`}>
        <div className="instructions-header">
          <h4>📖 Instructions</h4>
          <button 
            className="minimize-btn"
            onClick={() => setInstructionsMinimized(!instructionsMinimized)}
            title={instructionsMinimized ? "Expand instructions" : "Minimize instructions"}
          >
            {instructionsMinimized ? '📖' : '🔽'}
          </button>
        </div>
        <div className="instructions-content">
          <div className="instruction-group">
            <h4>🎮 Controls:</h4>
            <div className="instruction">🖱️ Click cards to navigate</div>
            <div className="instruction">⌨️ Arrow keys to navigate</div>
            <div className="instruction">⏎ Enter/Space to select</div>
            <div className="instruction">⎋ Escape to go back</div>
          </div>
          <div className="instruction-group">
            <h4>✋ Gestures:</h4>
            <div className="instruction">🤚 Swipe left/right to navigate</div>
            <div className="instruction">👉 Point finger to select</div>
            <div className="instruction">✌️ Peace sign to go back</div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default GestureInterface;